import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import CheckIcon from '@material-ui/icons/Check';

import { translate } from 'services/i18nService';
import IssuersPage from 'pages/IssuersPage/IssuersPage';
import LoadingButton from 'components/LoadingButton/LoadingButton';
import PaymentMethodIcon from 'components/PaymentMethodIcon/PaymentMethodIcon';
import { hasIssuers } from 'utils/helpers';

import cam4Logo from '../../../../images/logo/logo.png';

import styles from './PaymentMethodList.css';


function PaymentMethodList(props) {
  const {
    isSubmiting,
    product,
    onPayClick,
  } = props;

  const {
    amount,
    currency,
    item,
    months,
    price,
  } = product;

  const [showIssuersPage, setShowIssuersPage] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState({});

  const handlePayClick = useCallback((method) => {
    if (hasIssuers(method.type)) {
      setShowIssuersPage(true);
      setSelectedMethod(method);
    } else {
      onPayClick(method);
    }
  }, [onPayClick]);

  const onHandlePayClick = useCallback((method) => () => {
    handlePayClick(method);
  }, [handlePayClick]);

  const handleIssuers = useCallback((issuer) => {
    onPayClick({
      ...selectedMethod,
      issuerValue: issuer.value,
    });
  }, [onPayClick, selectedMethod]);

  if (showIssuersPage) {
    return (
      <IssuersPage
        isLoading={isSubmiting.loading}
        proceedRequest={handleIssuers}
        selectedPackage={product.item}
      />
    );
  }

  const renderDesc = (method) => {
    if (typeof method.type !== 'undefined') {
      return translate(`payment.processor.${method.processorId}_${method.type}.text`);
    }

    return translate(`payment.processor.${method.processorId}.text`);
  };

  const renderBtnText = (method) => {
    const PAYMENT_PROCESSOR_ID_THRESHOLD = 23;

    let displayName = undefined;
    if (method.processorId >= PAYMENT_PROCESSOR_ID_THRESHOLD) {
      displayName = translate(`payment.${method.type}.displayName`);
    } else if (typeof method.type !== 'undefined') {
      displayName = translate(`payment.processor.${method.processorId}_${method.type}.displayName`);
    } else {
      displayName = translate(`payment.processor.${method.processorId}.displayName`);
    }

    const methodPrefix = translate('payment.payment.method.prefix');

    return `${methodPrefix} ${displayName}`;
  };

  return (
    <section className={styles.content}>
      <header className={styles.headerWrap}>
        <img
          alt="logo"
          className={styles.cam4}
          id="cam4Logo"
          src={cam4Logo}
        />
        <div
          className={styles.secure}
          id="guaranteedSecurity"
        >
          <ul>
            <li>{translate('epay.logo.safe.and.secure')}</li>
            <li>{translate('epay.logo.quick.and.easy')}</li>
            <li>{translate('epay.logo.clear.and.simple')}</li>
          </ul>
          <p className={styles.slash}>
            {translate('epay.logo.guaranteed')}
          </p>
        </div>
      </header>
      <main className={styles.mainContent}>
        <div className={styles.categoryHeader}>
          <div className={styles.categoryHeaderIcon}>
            <CheckIcon />
          </div>
          <div className={styles.categoryHeaderContent}>
            <div>
              <Typography
                className={styles.mediumTitile}
                id="goldMembershipTitle"
              >
                {translate('epay.epay.gold.membership')}
              </Typography>
              <Typography
                className={styles.mediumSubtitle}
                id="pricePerMonth"
                paragraph={true}
              >
                {`${months} ${months !== 1 ? translate('cam4bucks.cam4bucks.248') : translate('epay.epay.gold.month')}`}
                ({translate('payment.v2.gold.pricePerMonth', { 0: `${currency}${price}` })})
              </Typography>
            </div>
            <div className={styles.categoryHeaderRight}>
              <Typography
                className={styles.categoryHeaderRightTitle}
                id="price"
              >
                {`${currency}${amount}`}
              </Typography>
            </div>
          </div>
        </div>

        <h4 className={styles.blackHead}>
          {translate('epay.choose.payment.method')}
        </h4>

        {product.item &&
          item.categories &&
          item.categories.length > 0 &&
          item.categories.map((cat) => (
            <div key={`cat-${cat.type}`} className={styles.categoryBox}>
              <h4 className={styles.categoryName}>
                {translate(`payment.payment.category.${cat.type}`)}
              </h4>
              {cat.methods.map((method) => (
                <Grid
                  key={`method-${method.type}`}
                  className={styles.borderItem}
                  container={true}
                  spacing={3}
                >
                  <Grid item={true} sm={8} xs={12}>
                    {cat.type !== 'CC' && (
                      <PaymentMethodIcon
                        className={styles.inlineImage}
                        prefixedIcon={{
                          methodType: method.type,
                          processorId: method.processorId,
                        }}
                      />
                    )}

                    <Typography className={styles.methodDesc}>
                      {renderDesc(method)}
                    </Typography>
                  </Grid>
                  <Grid item={true} sm={4} xs={12}>
                    <div className={styles.actionContainer}>
                      {cat.type === 'CC' && (
                        <span id="paymentMethodIcon">
                          <PaymentMethodIcon
                            className={styles.allIcons}
                            prefixedIcon={{
                              methodType: method.type,
                              processorId: method.processorId,
                            }}
                          />
                        </span>
                      )}
                      <div>
                        <LoadingButton
                          className={styles.fullButtonWidth}
                          colorVariant="green"
                          customContainerClass={styles.fullButtonWidth}
                          id={`paymentButton_${
                            method.type ? `${method.processorId}_${method.type}` : method.processorId
                          }`}
                          isLoading={
                            isSubmiting.loading && method.type === isSubmiting.type ? isSubmiting.loading : false
                          }
                          size="small"
                          onClick={onHandlePayClick(method)}
                        >
                          {renderBtnText(method)}
                        </LoadingButton>
                      </div>
                    </div>
                  </Grid>
                </Grid>
              ))}
            </div>
          ))}
      </main>
      <footer className={styles.footer}>
        <h3>{translate('cam4bucks.cam4bucks.86')}</h3>
        <p>
          {translate('epay.epay.footer.text.5')}{' '}
          <a
            href="https://epoch.com/billing_support"
            id="epochPaymentSupport"
            rel="noopener noreferrer"
            target="_blank"
          >
            {translate('epay.epay.footer.link.5')}
          </a>
        </p>
        <p className={styles.supportSmall}>
          {translate('epay.epay.checkout.support.text.4')}{' '}
          <a href="mailto:technical@cam4.com" id="cam4TechnicalSupport">
            technical@cam4.com
          </a>
          .
        </p>
      </footer>
    </section>
  );
}

PaymentMethodList.propTypes = {
  isSubmiting: PropTypes.object,
  product: PropTypes.object,
  onPayClick: PropTypes.func,
};

PaymentMethodList.defaultProps = {
  isSubmiting: undefined,
  product: undefined,
  onPayClick: () => undefined,
};

export default PaymentMethodList;
